import Swiper from 'swiper';
import { Pagination, Thumbs, Manipulation, Zoom } from 'swiper/modules';

class ProductGalleryV2 extends HTMLElement {
    constructor() {
        super();

        this.mainImages = this.querySelectorAll('.main-image');
        this.thumbnails = this.querySelectorAll('.thumbnail');
        this.variantImages = JSON.parse(this.querySelector('#variant-images').innerHTML);
        this.swiperContainer = this.querySelector('.swiper-container');
        this.thumbsContainer = this.querySelector('.gallery-thumbnails');
        this.paginationEl = this.querySelector('.swiper-pagination');
        this.galleryThumbs = this.querySelector('.gallery-thumbnails');
        this.swiper = null;
        this.thumbsSwiper = null;
        this.variantImageToggle = true
        this.initGallery();
        this.slidesRemaining = 0;
        this.isMerch = this.dataset.isMerch === 'true';
        this._onVariantChanged = this._onVariantChanged.bind(this);
    }

    connectedCallback() {
        document.addEventListener('variant-changed', this._onVariantChanged, { capture: true });

        this.isMerch = this.dataset.isMerch === 'true';

        const videoContainers = document.querySelectorAll('.video-container')

        this.createVideoClickHandler(videoContainers);

        document.querySelectorAll('.main-image').forEach(picture => {
            if (picture.dataset.src) {
                picture.src = picture.dataset.src;
            }

            picture.querySelectorAll('source').forEach(source => {
                if (source.dataset.srcset) {
                    source.srcset = source.dataset.srcset;
                }
            });

            picture.querySelectorAll('img').forEach(img => {
                if (img.dataset.src) {
                    img.src = img.dataset.src;
                }
            });

            picture.classList.remove('hidden');
        });
    }

    createVideoClickHandler(videoContainers) {
        videoContainers.forEach(container => {
            container.addEventListener('click', () => {
              const videoId = container.dataset.videoId;
        
              // Create YouTube iframe
              const iframe = document.createElement('iframe');
              iframe.src = `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&rel=0&controls=2&modestbranding=0&showinfo=0`;
              iframe.width = '100%';
              iframe.height = '100%';
              iframe.allowFullscreen = true;
              iframe.setAttribute('title', container.querySelector('img').alt);
        
              // Replace placeholder with iframe
              container.innerHTML = '';
              container.appendChild(iframe);
            });
        });
    }

    removeVideoClickHandler(videoContainers) {
        videoContainers.forEach(container => {
            container.removeEventListener('click', this.videoClickHandler);
        });
    }

    disconnectedCallback() {
        document.removeEventListener('variant-changed', this._onVariantChanged);
    }

    _onVariantChanged(evt) {

        if (!this.variantImageToggle) {
            return
        }

        if (this.isMerch && !this.variantImageToggle) {
            this.slideToVariantImage(evt.detail.variant);
            return;
        }

        this.rebuildGallery(evt.detail.variant);
    }

    rebuildGallery(variant) {
       if (!variant || !variant.id) {
            return;
        }

        const variantId = Number(variant.id);

        const variantImages = this.variantImages.find(v => Number(v.id) === variantId)?.images || [];

        let videoContainers = document.querySelectorAll('.video-container');

        this.removeVideoClickHandler(videoContainers);

        if (variantImages.length === 0) {
            return;
        }

        const swiper = this.swiper;
        const thumbs = this.thumbsSwiper;
        const allMainSlides    = this.swiper.slides;
        const videoMainIndex     = allMainSlides.findIndex(sl => sl.classList.contains('video-container'));
        const allThumbSlides   = this.thumbsSwiper.slides;

        const paired = [];
        for (let i = 0; i < variantImages.length; i += 2) {
            paired.push({
                id:  variantImages[i],
                url: variantImages[i + 1],
            });
        }

        // Update image src of existing slides and remove extras
        let mainImageSlides = allMainSlides.filter((_, i) => i !== videoMainIndex);

        swiper.removeSlide(videoMainIndex);

        mainImageSlides.forEach((slide, i) => {
            slide.classList.add('main-image');
            const imgEl = slide.querySelector('img');
            if (i < paired.length) {
                imgEl.src = paired[i].url;
                imgEl.srcset = paired[i].url;
                imgEl.dataset.variantId = paired[i].id;
            } else {
                swiper.removeSlide(allMainSlides.indexOf(slide));
            }
        });

        // Append new main images, if any
        if (paired.length > mainImageSlides.length) {

            if (this.isMerch) {
                paired.slice(mainImageSlides.length).forEach((pair, i) => {
                    const pos = mainImageSlides.length + i + (videoMainIndex >= 0 && mainImageSlides.length + i >= videoMainIndex ? 1 : 0);
                    const variantId = pair.id
                    swiper.addSlide(pos, `<div class="swiper-slide main-image !aspect-square">
                        <div class="zoom-in zoom-icon absolute bottom-0 right-0 z-10 p-5">
                            <svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-plus" width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path fill-rule="evenodd" clip-rule="evenodd" d="M4.66724 7.93978C4.66655 7.66364 4.88984 7.43922 5.16598 7.43853L10.6996 7.42464C10.9758 7.42395 11.2002 7.64724 11.2009 7.92339C11.2016 8.19953 10.9783 8.42395 10.7021 8.42464L5.16849 8.43852C4.89235 8.43922 4.66793 8.21592 4.66724 7.93978Z" fill="currentColor"/>
                                <path fill-rule="evenodd" clip-rule="evenodd" d="M7.92576 4.66463C8.2019 4.66394 8.42632 4.88723 8.42702 5.16337L8.4409 10.697C8.44159 10.9732 8.2183 11.1976 7.94215 11.1983C7.66601 11.199 7.44159 10.9757 7.4409 10.6995L7.42702 5.16588C7.42633 4.88974 7.64962 4.66532 7.92576 4.66463Z" fill="currentColor"/>
                                <path fill-rule="evenodd" clip-rule="evenodd" d="M12.8324 3.03011C10.1255 0.323296 5.73693 0.323296 3.03011 3.03011C0.323296 5.73693 0.323296 10.1256 3.03011 12.8324C5.73693 15.5392 10.1255 15.5392 12.8324 12.8324C15.5392 10.1256 15.5392 5.73693 12.8324 3.03011ZM2.32301 2.32301C5.42035 -0.774336 10.4421 -0.774336 13.5395 2.32301C16.6101 5.39361 16.6366 10.3556 13.619 13.4588L18.2473 18.0871C18.4426 18.2824 18.4426 18.599 18.2473 18.7943C18.0521 18.9895 17.7355 18.9895 17.5402 18.7943L12.8778 14.1318C9.76383 16.6223 5.20839 16.4249 2.32301 13.5395C-0.774335 10.4421 -0.774335 5.42035 2.32301 2.32301Z" fill="currentColor"/>
                            </svg>
                        </div>
                        <div class="zoom-out zoom-icon absolute bottom-0 right-0 z-10 p-5">
                            <svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-minus" width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path fill-rule="evenodd" clip-rule="evenodd" d="M4.66724 7.93978C4.66655 7.66364 4.88984 7.43922 5.16598 7.43853L10.6996 7.42464C10.9758 7.42395 11.2002 7.64724 11.2009 7.92339C11.2016 8.19953 10.9783 8.42395 10.7021 8.42464L5.16849 8.43852C4.89235 8.43922 4.66793 8.21592 4.66724 7.93978Z" fill="currentColor"/>
                                <path fill-rule="evenodd" clip-rule="evenodd" d="M12.8324 3.03011C10.1255 0.323296 5.73693 0.323296 3.03011 3.03011C0.323296 5.73693 0.323296 10.1256 3.03011 12.8324C5.73693 15.5392 10.1255 15.5392 12.8324 12.8324C15.5392 10.1256 15.5392 5.73693 12.8324 3.03011ZM2.32301 2.32301C5.42035 -0.774336 10.4421 -0.774336 13.5395 2.32301C16.6101 5.39361 16.6366 10.3556 13.619 13.4588L18.2473 18.0871C18.4426 18.2824 18.4426 18.599 18.2473 18.7943C18.0521 18.9895 17.7355 18.9895 17.5402 18.7943L12.8778 14.1318C9.76383 16.6223 5.20839 16.4249 2.32301 13.5395C-0.774335 10.4421 -0.774335 5.42035 2.32301 2.32301Z" fill="currentColor"/>
                            </svg>
                        </div>
                        <div class="swiper-zoom-container"><img src="${pair.url}" srcset="${pair.url}" class="object-cover h-full w-full" data-variant-id="${variantId}"/>
                        </div>
                        </div>`);
                });
            } else {
                paired.slice(mainImageSlides.length).forEach((pair, i) => {
                    const pos = mainImageSlides.length + i + (videoMainIndex >= 0 && mainImageSlides.length + i >= videoMainIndex ? 1 : 0);
                    const variantId = pair.id
                    swiper.addSlide(pos, `<div class="swiper-slide main-image !aspect-square"><img src="${pair.url}" srcset="${pair.url}" class="object-contain" data-variant-id="${variantId}"/></div>`);
                });
            }
        }

        // Append video to main image slides
        if (videoMainIndex >= 0) {
            const videoSlideEl = allMainSlides[videoMainIndex].cloneNode(true);
            swiper.addSlide(videoMainIndex, videoSlideEl);
        }

        const videoThumbHTML = allThumbSlides.findIndex(sl => sl.classList.contains('video-thumbnail'));

        thumbs.removeAllSlides();
        paired.forEach((pair, i) => {
            const slot = i < 2 ? i : i + 1;
            thumbs.addSlide(slot,
                `<div class="swiper-slide !aspect-square">
                <img
                class="thumbnail cursor-pointer !aspect-square object-contain"
                data-index="${i}"
                src="${pair.url}"
                srcset="${pair.url}"
                /></div>
                `
            );
        });

        if (videoThumbHTML >= 0) {
            const videoThumbEl = allThumbSlides[videoThumbHTML].cloneNode(true);
            thumbs.addSlide(2, videoThumbEl);
        }

        const oldIdx = swiper.slides.findIndex(sl => sl.classList.contains('video-container'));
        if (oldIdx > -1) {
            // const videoHTML = swiper.slides.findIndex(sl => sl.classList.contains('video-container'));
            // swiper.removeSlide(oldIdx);
            // swiper.addSlide(2, videoHTML);

            const videoSlideEl = swiper.slides[oldIdx];
            const clonedSlide = videoSlideEl.cloneNode(true);
            
            swiper.removeSlide(oldIdx);
            swiper.addSlide(100, clonedSlide);

        }

        // Reset aspect ratio for main images
        this.swiper.slides.forEach((slide, i) => {
            slide.children[0].setAttribute('style', 'aspect-ratio:1/1!important;');
        });

        // Reset data index for thumbnails
        const imageSlides = this.thumbsSwiper.slides.filter(slide => !slide.classList.contains('thumbnail'));
        imageSlides.forEach((slide, i) => {
            slide.dataset.index = i;
        });


        this.thumbnails = this.querySelectorAll('.thumbnail');
        // this.removeThumbnailEventListeners();
        this.attachThumbnailEventListeners();

        swiper.update();
        thumbs.update();
        this.slideToVariantImage(variant);
        this.updateLastThumbnailOverlay();

        videoContainers = document.querySelectorAll('.video-container');

        this.createVideoClickHandler(videoContainers);
    }

    slideToVariantImage(variant) {
        this.mainImages = this.querySelectorAll('.main-image');
        const mainImages = Array.from(this.mainImages);

        let selectedVariantImage = mainImages.find(slide => {
            const img = slide.querySelector('img');
            let variantIdString = String(variant.id);
            return img.dataset.variantId.includes(variantIdString);
        })


        if (!selectedVariantImage || selectedVariantImage.dataset.variantId === null) {
            this.swiper.slideTo(0);
            return;
        }
        const dataIndex = Number(selectedVariantImage.querySelector('img').dataset.index);

        this.swiper.slideTo(dataIndex);
    }

    initGallery() {
        const self = this;
        // Initialize the thumbnails Swiper for the thumbnail gallery
        this.thumbsSwiper = new Swiper(this.galleryThumbs, {
            modules: [Manipulation],
            slidesPerView: '5', // Automatically adjust the number of visible thumbnails
            spaceBetween: 8, // Space between thumbnails
            loop: false, // Loop through thumbnails
            freeMode: true, // Free scrolling for thumbnails
            watchSlidesVisibility: true, // Track visible slides
            watchSlidesProgress: true, // Track progress of visible slides
            breakpoints: {
                1024: {
                    spaceBetween: 12,
                }
            },
            on: {
                resize: () => {
                    self.updateLastThumbnailOverlay();
                },
                slideChange: function () {
                    self.updateLastThumbnailOverlay();
                    self.unhideThumbnails();
                },
                click: () => {
                    self.unhideThumbnails();
                }
            },
        });

        this.updateLastThumbnailOverlay();

        // Initialize the main image Swiper
        this.swiper = new Swiper(this.swiperContainer, {
            modules: [Pagination, Thumbs, Manipulation, Zoom],
            zoom: {
                maxRatio: 5,
                minRatio: 1,
                panOnlyInZoom: true,
              },
            touchRatio: 0.5,
            simulateTouch: true,
            passiveListeners: false,
            pagination: {
                el: this.paginationEl,
                clickable: true,
            },
            spaceBetween: 10, // Space between slides
            slidesPerView: 1, // One image per slide
            initialSlide: 0,
            autoHeight: false,
            loop: false, // Disable looping for main gallery
            on: {
                sliderFirstMove: () => {
                    self.mainImages.forEach((picture) => {
                        picture.classList.remove('hidden');
                    });

                     self.unhideThumbnails();   
                },
                zoomChange: () => {
                    self.unhideThumbnails();
                },
                slideChange: function () {
                    // Check the active slide
                    setTimeout(() => {
                        const activeSlide = document.querySelector('.swiper-slide-active');

                        // Check if the active slide contains the video-container class
                        if (activeSlide && activeSlide.classList.contains('video-container')) {
                            // Find the product-badge-container
                            const badgeContainer = document.querySelector('.product-badge-container');

                            // Add the video-active class if badgeContainer exists
                            if (badgeContainer) {
                                badgeContainer.classList.add('video-active');
                            }
                        } else {
                            // Remove the video-active class if no longer on the video slide
                            const badgeContainer = document.querySelector('.product-badge-container');
                            if (badgeContainer) {
                                badgeContainer.classList.remove('video-active');
                            }
                        }

                        // self.updateThumbnailBorder(self.swiper.activeIndex);
                    }, 200);
                }, 
            },
            thumbs: {
                swiper: this.thumbsSwiper,
            }
        });


        // Update thumbnail border when clicked
        if (this.thumbnails.length > 0) {
            this.attachThumbnailEventListeners();
        }


        if (this.dataset.isMerch !== 'true') return;

          // Merch zoom handling - Zoom icons
        this.swiper.el.addEventListener(
            'pointerdown',
            (ev) => {
            if (ev.target.closest('.zoom-icon')) {
                ev.preventDefault();
                ev.stopPropagation();
            }
            },
            true
        );
        
        this.swiper.el.addEventListener('click', (ev) => {
            const btn = ev.target.closest('.zoom-icon');
            if (!btn) return;
        
            ev.preventDefault();
            ev.stopPropagation();
        
            const scale = this.swiper.zoom?.scale ?? 1;
        
            if (btn.classList.contains('zoom-out')) {
            this.swiper.zoom.out();
            return;
            }
        
            // zoom-in icon => always center
            if (scale > 1) this.swiper.zoom.out();
            else this.zoomToCenter(this.swiper, 5);
        }, true);
            

        // Merch zoom handling - Excl zoom icons
        this.swiper.on('click', (swiper, e) => {
            const target = e.target;
            if (target?.closest?.('.swiper-pagination')) return;
            if (target?.closest?.('.zoom-icon')) return;
          
            const scale = swiper.zoom?.scale ?? 1;
            if (scale > 1) swiper.zoom.out();
            else swiper.zoom.in(e, 5);
          });
          
    }

    zoomToCenter(swiper, ratio = 5) {
        const zoomEl =
          swiper.slides[swiper.activeIndex]?.querySelector('.swiper-zoom-container') ||
          swiper.el.querySelector('.swiper-slide-active .swiper-zoom-container');
        if (!zoomEl) return;
      
        const zoomTarget = zoomEl.querySelector('.swiper-zoom-target') || zoomEl;
      
        requestAnimationFrame(() => {
          const r = zoomEl.getBoundingClientRect();
          const cx = r.left + r.width / 2;
          const cy = r.top + r.height / 2;
      
          const evt = {
            clientX: cx,
            clientY: cy,
            pageX: cx + window.scrollX,
            pageY: cy + window.scrollY,
            target: zoomTarget,
            originalEvent: {
              clientX: cx,
              clientY: cy,
              pageX: cx + window.scrollX,
              pageY: cy + window.scrollY,
              target: zoomTarget,
            },
          };
      
          swiper.zoom.in(evt, ratio);
        });
      }
      
      

    unhideThumbnails() {
        const slides = this.thumbsSwiper.slides;
        slides.forEach(slide => {
            const img = slide.querySelector('img');
            if (img && img.classList.contains('hidden')) {
              img.classList.remove('hidden');
            }
          });
    }
      

    attachThumbnailEventListeners() {
        this.thumbnails.forEach((thumbnail) => {
            thumbnail.addEventListener('click', (e) => {
                const slideIndex = Number(thumbnail.dataset.index);
             
                this.swiper.slideTo(slideIndex);
               
                // this.updateThumbnailBorder(slideIndex);
            });
        });
    }

    updateLastThumbnailOverlay() {
        const activeIndex = this.thumbsSwiper.activeIndex;

        const slidesPerView = Math.floor(this.thumbsSwiper.params.slidesPerView);
        let lastVisibleIndex = null
        const thumbnailVideo = this.querySelector('.video-thumbnail');

        const slides = this.thumbsSwiper.slides;
        const imageSlides = slides.filter(slide => !slide.classList.contains('video-thumbnail'));
        const totalSlides = slides.length;

        let slidesRemaining = null

        if (thumbnailVideo) {
            lastVisibleIndex = activeIndex + slidesPerView - 1;
            slidesRemaining = Math.max(totalSlides - (lastVisibleIndex + 1), 0);
        } else {
            lastVisibleIndex = activeIndex + slidesPerView;
            slidesRemaining = Math.max(totalSlides - (lastVisibleIndex), 0);
        }


        // First, remove any existing overlay from all slides
        slides.forEach(slide => {
            const existing = slide.querySelector('.last-thumbnail-overlay');
            if (existing) existing.remove();
            slide.removeEventListener('click', () => {
                this.thumbsSwiper.slideTo(lastVisibleIndex+1);
            });
        });

        if (slidesRemaining > 0 && lastVisibleIndex < totalSlides) {
            
            const lastVisibleSlide = imageSlides[lastVisibleIndex - 1];

            // Create a fresh overlay element
            const overlay = document.createElement('div');
            overlay.className = 'last-thumbnail-overlay';
            overlay.textContent = `+${slidesRemaining}`;

            lastVisibleSlide.style.position = 'relative';
            lastVisibleSlide.appendChild(overlay);

            lastVisibleSlide.addEventListener('click', () => {
                this.thumbsSwiper.slideTo(lastVisibleIndex+1);
            });
        }
    }

}

customElements.define('product-gallery-v2', ProductGalleryV2);
